import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../styles/success.scss';
import ShopifyShop from '../components/shopify/shopify_shop';
import config from '../config/config';

const DEFAULT_COLLECTION_IDS = config.DEFAULT_COLLECTION_IDS || [];
const EVENT_COLLECTION_IDS = config.EVENT_COLLECTION_IDS || [];
const SESSION_KEY = 'mioo_booking_confirmed';

/** Persist the confirmed booking snapshot so the page survives a refresh. */
const saveToSession = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* storage full or disabled – non-critical */
  }
};

const loadFromSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

function Success() {
  const { t, i18n } = useTranslation();
  const service = useSelector((state) => state.service);
  const history = useHistory();

  // On a fresh page-load Redux is empty; fall back to sessionStorage
  const persisted = useMemo(() => loadFromSession(), []);

  const isConfirmed = service.success || !!persisted;

  // Persist whenever Redux has fresh booking data (triggers on every new booking)
  useEffect(() => {
    if (service.success) {
      saveToSession({
        user: service.confirmedUser ?? service.user,
        booking: service.confirmedBooking ?? service.booking,
        shop: service.confirmedShop ?? service.shop,
        event: service.event ?? null,
        bookingResponse: service.bookingResponse ?? null,
      });
    }
  }, [service.success, service.bookingResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect only when there is nothing – neither Redux nor session
  if (!isConfirmed) {
    history.push('/');
    return null;
  }

  // Merge: prefer Redux (live), fall back to persisted (after refresh).
  // NOTE: Do NOT fall back to service.user / service.booking / service.shop here –
  // after a refresh those are just the reducer's INITIAL_STATE (e.g. date: new Date())
  // and would shadow the real values stored in sessionStorage.
  const user = service.confirmedUser ?? persisted?.user;
  const booking = service.confirmedBooking ?? persisted?.booking;
  const shop = service.confirmedShop ?? persisted?.shop;
  const event = service.event ?? persisted?.event;
  const bookingResponse = service.bookingResponse ?? persisted?.bookingResponse;

  // Ensure moment uses current language for month names
  moment.locale(i18n.resolvedLanguage || 'sv');

  const isEventBooking = !!(bookingResponse?.event_booking_id || bookingResponse?.id);
  const resolvedServiceId = bookingResponse?.service_id ?? booking?.service_id ?? shop?.id;
  const useEventCollections = isEventBooking && EVENT_COLLECTION_IDS.length > 0;

  return (
    <div className="container success-container">
      <h1>
        {event ? t('success_thanks', 'Thanks!') : t('booking_success_title', 'Du har bokat en tid')}
      </h1>
      {event ? (
        <p>{t('success_email_details', 'You will receive more details via email shortly.')}</p>
      ) : (
        <>
          <p className="details m-0 p-0 mt-3 ">{`${user?.address?.value ?? shop?.address ?? ''}, ${
            user?.address?.postcode ?? shop?.postcode ?? ''
          }`}</p>
          <p className="details">
            {moment(booking?.date).format('D MMMM') + ', ' + (booking?.service_slot_name ?? '')}
          </p>
          <div className="line" />
          <p className="info">
            {t(
              'booking_success_email_details',
              'Vi har även skickat en bekräftelse med detaljer och mer info till din e-postadress.'
            )}
          </p>
        </>
      )}

      {/* ── Mini Shopify web-shop ──
           Hidden when: event with display_webshop=false,
           or would fall back to DEFAULT_COLLECTION_IDS but service_id is missing */}
      {!(isEventBooking && event?.displayWebshop === false) &&
        (useEventCollections || (!isEventBooking && !!resolvedServiceId)) && (
          <ShopifyShop
            collectionIds={useEventCollections ? EVENT_COLLECTION_IDS : DEFAULT_COLLECTION_IDS}
            bookingContext={{
              userId: event?.buddyId ?? shop?.id ?? null,
              serviceId: resolvedServiceId,
              eventBookingId: bookingResponse?.event_booking_id ?? bookingResponse?.id,
              buyingUserId: bookingResponse?.user_id ?? bookingResponse?.buying_user_id,
              buyingUserEmail:
                bookingResponse?.email ??
                bookingResponse?.given_email ??
                bookingResponse?.buying_user_email ??
                user?.email ??
                user?.given_email,
            }}
          />
        )}
    </div>
  );
}

export default Success;
